import { CircleHelp } from 'lucide-react';
import { useLanguage } from '@contexts/LanguageContext';
import { resolveTourCopy } from './tour-copy';
import { GLOBAL_TOUR_ID, tourById } from './tour-registry';
import { useOptionalTour } from './TourProvider';
import { useOptionalOnboardingGate } from '@/features/first-login/gate-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/lumen/dropdown-menu';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';

interface TourHelpButtonProps {
  /** Sidebar rail is collapsed: icon only, tooltip carries the label. */
  isMinimized?: boolean;
  /** Shared with the sidebar's other footer controls so it looks native. */
  className?: string;
  /**
   * `toolbar` for pages that hide the sidebar entirely — the PIB editor, the
   * review screen, export. Those are exactly the screens with the most tours,
   * and the sidebar-docked control is simply not on them.
   */
  variant?: 'sidebar' | 'toolbar';
  /** Emit the `tour-help-pill` anchor. A preview copy of the rail passes false. */
  withAnchor?: boolean;
}

/**
 * "Panduan" — replays the guidance for whatever the user is looking at.
 *
 * Docked in the sidebar footer next to the theme and language controls, rather
 * than floating over the page. A floating pill has to dodge whatever the page
 * puts in the corner, and it lost that fight repeatedly: it covered Process
 * Dokumen on the upload screen, and once it moved clear of the action bar it
 * simply hovered over the panel content instead. A docked control cannot
 * collide with anything, and it sits where users already look for app-level
 * switches.
 */
export const TourHelpButton = ({
  isMinimized = false,
  className,
  variant = 'sidebar',
  withAnchor = true,
}: TourHelpButtonProps) => {
  const tour = useOptionalTour();
  const onboardingGate = useOptionalOnboardingGate();
  const { language } = useLanguage();

  if (!tour) {
    return null;
  }

  const { pageTour, subTour, subTourLabel, sections, startTour, startTourAt, startTourById } = tour;
  const globalTour = tourById(GLOBAL_TOUR_ID);
  const t = (key: string, vars?: Record<string, string | number>) => resolveTourCopy(language, key, vars);

  const isToolbar = variant === 'toolbar';

  return (
    <DropdownMenu>
      <MenuTarget isToolbar={isToolbar} isMinimized={isMinimized} withAnchor={withAnchor} className={className}>
        <CircleHelp size={isToolbar ? 16 : 15} strokeWidth={1.5} className="shrink-0" />
        {!isMinimized && !isToolbar && (
          <span className="text-[13px] truncate flex-1 text-left text-fg">{t('pill.label')}</span>
        )}
      </MenuTarget>
      <DropdownMenuContent
        side={isToolbar ? 'bottom' : isMinimized ? 'right' : 'top'}
        align="end"
        sideOffset={8}
        className="w-60"
      >
        <DropdownMenuGroup>
          {subTour && (
            <DropdownMenuItem onSelect={() => startTour(subTour)}>
              {t('pill.tabGuide', { tab: subTourLabel ?? '' })}
            </DropdownMenuItem>
          )}
          {pageTour && <DropdownMenuItem onSelect={() => startTour(pageTour)}>{t('pill.pageGuide')}</DropdownMenuItem>}
          {pageTour &&
            sections.map((section) => (
              <DropdownMenuItem key={section.key} onSelect={() => startTourAt(pageTour, section.stepKey)}>
                {t(`pill.section.${section.key}`)}
              </DropdownMenuItem>
            ))}
        </DropdownMenuGroup>
        {globalTour && (
          <DropdownMenuItem onSelect={() => startTourById(GLOBAL_TOUR_ID)} className="text-fg-muted">
            {t('pill.menuGuide')}
          </DropdownMenuItem>
        )}
        {onboardingGate && (
          <DropdownMenuItem onSelect={onboardingGate.openOnboarding} className="text-fg-muted">
            {t('pill.onboarding')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Menu.Target wrapper that handles the toolbar vs sidebar styling, the tooltip,
 * and the data-tour anchor. The trigger must be `asChild` so the dropdown
 * Radix layer delegates to the button below.
 */
function MenuTarget({
  isToolbar,
  isMinimized,
  withAnchor,
  className: outerClass,
  children,
}: {
  isToolbar: boolean;
  isMinimized: boolean;
  withAnchor: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const { language } = useLanguage();
  const t = (key: string, vars?: Record<string, string | number>) => resolveTourCopy(language, key, vars);

  const tooltipSide = isToolbar ? 'bottom' : isMinimized ? 'right' : undefined;
  const showTooltip = tooltipSide !== undefined;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            // Anchor only on the sidebar copy. Pages that "hide" the sidebar do
            // it with display:none, so its button stays in the DOM — tagging
            // both would give querySelector two matches and let a tour step
            // spotlight the invisible one.
            {...(withAnchor && !isToolbar ? { 'data-tour': 'tour-help-pill' } : {})}
            aria-label={t('pill.aria')}
            className={
              isToolbar
                ? 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-line bg-raised text-fg-2 transition-colors hover:bg-surface-2'
                : cn('bg-transparent text-fg', outerClass)
            }
          >
            {children}
          </button>
        </DropdownMenuTrigger>
      </TooltipTrigger>
      {showTooltip && (
        <TooltipContent side={tooltipSide} sideOffset={8}>
          {t('pill.label')}
        </TooltipContent>
      )}
    </Tooltip>
  );
}
